import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

interface CompanyAssetDialogProps {
  open: boolean;
  onClose: () => void;
  asset: any;
}

export const CompanyAssetDialog = ({ open, onClose, asset }: CompanyAssetDialogProps) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    asset_name: "",
    asset_category: "",
    supplier_id: "",
    purchase_date: "",
    purchase_price: "",
    current_value: "",
    depreciation_rate: "",
    location: "",
    serial_number: "",
    condition: "good",
    maintenance_schedule: "",
    last_maintenance_date: "",
    next_maintenance_date: "",
    notes: "",
  });

  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (asset) {
      setFormData({
        asset_name: asset.asset_name || "",
        asset_category: asset.asset_category || "",
        supplier_id: asset.supplier_id || "",
        purchase_date: asset.purchase_date || "",
        purchase_price: asset.purchase_price || "",
        current_value: asset.current_value || "",
        depreciation_rate: asset.depreciation_rate || "",
        location: asset.location || "",
        serial_number: asset.serial_number || "",
        condition: asset.condition || "good",
        maintenance_schedule: asset.maintenance_schedule || "",
        last_maintenance_date: asset.last_maintenance_date || "",
        next_maintenance_date: asset.next_maintenance_date || "",
        notes: asset.notes || "",
      });
    }
  }, [asset]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const dataToSave = {
        ...formData,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        current_value: formData.current_value ? parseFloat(formData.current_value) : null,
        depreciation_rate: formData.depreciation_rate ? parseFloat(formData.depreciation_rate) : null,
        supplier_id: formData.supplier_id || null,
      };

      if (asset) {
        const { error } = await supabase
          .from('company_assets')
          .update(dataToSave)
          .eq('id', asset.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('company_assets')
          .insert([{ ...dataToSave, admin_id: user.id }]);
        if (error) throw error;
      }

      toast.success(t('savedSuccessfully'));
      onClose();
    } catch (error) {
      console.error('Error saving asset:', error);
      toast.error(t('errorSavingData'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{asset ? t('editAsset') : t('addAsset')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('assetName')} *</Label>
              <Input
                value={formData.asset_name}
                onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>{t('assetCategory')} *</Label>
              <Select
                value={formData.asset_category}
                onValueChange={(v) => setFormData({ ...formData, asset_category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vehicle">{t('vehicle')}</SelectItem>
                  <SelectItem value="building">{t('building')}</SelectItem>
                  <SelectItem value="equipment">{t('equipment')}</SelectItem>
                  <SelectItem value="furniture">{t('furniture')}</SelectItem>
                  <SelectItem value="other">{t('other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('supplier')}</Label>
              <Select
                value={formData.supplier_id}
                onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('selectSupplier')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{t('noSupplier')}</SelectItem>
                  {suppliers?.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('serialNumber')}</Label>
              <Input
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('purchaseDate')}</Label>
              <Input
                type="date"
                value={formData.purchase_date}
                onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('purchasePrice')}</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.purchase_price}
                onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('currentValue')}</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.current_value}
                onChange={(e) => setFormData({ ...formData, current_value: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('depreciationRate')}</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.depreciation_rate}
                onChange={(e) => setFormData({ ...formData, depreciation_rate: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('location')}</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('condition')}</Label>
              <Select
                value={formData.condition}
                onValueChange={(v) => setFormData({ ...formData, condition: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent">{t('excellent')}</SelectItem>
                  <SelectItem value="good">{t('good')}</SelectItem>
                  <SelectItem value="fair">{t('fair')}</SelectItem>
                  <SelectItem value="poor">{t('poor')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('lastMaintenanceDate')}</Label>
              <Input
                type="date"
                value={formData.last_maintenance_date}
                onChange={(e) => setFormData({ ...formData, last_maintenance_date: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('nextMaintenanceDate')}</Label>
              <Input
                type="date"
                value={formData.next_maintenance_date}
                onChange={(e) => setFormData({ ...formData, next_maintenance_date: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('maintenanceSchedule')}</Label>
              <Textarea
                value={formData.maintenance_schedule}
                onChange={(e) => setFormData({ ...formData, maintenance_schedule: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t('cancel')}</Button>
            <Button type="submit">{t('save')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};